import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, partyAPI, formatCurrency, gstReturnAPI, tdsReturnAPI, transactionAPI, accountingAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Receipt, FileText, DollarSign, Clock, AlertTriangle, Plus, ArrowRight, ArrowUpCircle, ArrowDownCircle, Wallet, Users, Search, X, IndianRupee, FileCheck, TrendingUp, Settings2, Eye, EyeOff, ChevronUp, ChevronDown, ClipboardList, BookOpen, Landmark } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from 'recharts';
import api from '../services/api';

const CHART_COLORS = ['#9400D3', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

const ALL_WIDGETS = [
  { id: 'metrics', label: 'Key Metrics', pinned: true, order: 0 },
  { id: 'financial', label: 'Receivables / Payables', pinned: true, order: 1 },
  { id: 'tax_overview', label: 'GST / TDS / Advances', pinned: true, order: 2 },
  { id: 'revenue_chart', label: 'Revenue Chart', pinned: true, order: 3 },
  { id: 'status_pie', label: 'Invoice Status Pie', pinned: true, order: 4 },
  { id: 'quick_journal', label: 'Quick Journal Entry', pinned: false, order: 5 },
  { id: 'recent_journals', label: 'Recent Journal Entries', pinned: false, order: 6 },
  { id: 'quick_actions', label: 'Quick Actions Panel', pinned: true, order: 7 },
  { id: 'employee_activity', label: 'Employee Activity', pinned: false, order: 8 },
  { id: 'recent_receipts', label: 'Recent Receipts', pinned: true, order: 9 },
  { id: 'recent_invoices', label: 'Recent Invoices', pinned: true, order: 10 },
  { id: 'party_filter', label: 'Party Filter', pinned: false, order: 11 },
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [widgets, setWidgets] = useState(ALL_WIDGETS);
  const [showCustomize, setShowCustomize] = useState(false);
  const [partyFilter, setPartyFilter] = useState('');
  const [parties, setParties] = useState([]);
  const [gstPayable, setGstPayable] = useState(null);
  const [tdsData, setTdsData] = useState(null);
  const [txSummary, setTxSummary] = useState(null);
  const [recentJournals, setRecentJournals] = useState([]);
  const [jeForm, setJeForm] = useState({ date: new Date().toISOString().split('T')[0], narration: '', debit_account: '', credit_account: '', amount: '' });
  const [jeSaving, setJeSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const loadStats = useCallback((pn) => {
    const params = pn ? { party_name: pn } : {};
    dashboardAPI.stats(params).then(res => setStats(res.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadStats();
    partyAPI.list().then(r => setParties(r.data || [])).catch(() => {});
    gstReturnAPI.gstr3b({}).then(r => setGstPayable(r.data?.net_tax_payable)).catch(() => {});
    tdsReturnAPI.summary({}).then(r => setTdsData(r.data?.summary)).catch(() => {});
    transactionAPI.list({ limit: 1 }).then(r => setTxSummary(r.data?.summary)).catch(() => {});
    dashboardAPI.getWidgets().then(r => {
      const saved = r.data?.widgets || [];
      if (saved.length > 0) {
        // Merge saved with ALL_WIDGETS to pick up new widgets
        const merged = ALL_WIDGETS.map(aw => {
          const s = saved.find(sw => sw.id === aw.id);
          return s ? { ...aw, ...s } : aw;
        });
        setWidgets(merged);
      }
    }).catch(() => {});
    accountingAPI.chartOfAccounts().then(r => setAccounts(r.data?.accounts || [])).catch(() => {});
    api.get('/accounting/journal-entries', { params: { limit: 5 } }).then(r => setRecentJournals(r.data?.entries || [])).catch(() => {});
  }, [loadStats]);

  const applyPartyFilter = (name) => { setPartyFilter(name); setLoading(true); loadStats(name); };

  const isPinned = (id) => { const w = widgets.find(w => w.id === id); return w ? w.pinned !== false : true; };

  const toggleWidget = (id) => setWidgets(prev => prev.map(w => w.id === id ? { ...w, pinned: !w.pinned } : w));

  const moveWidget = (id, dir) => {
    setWidgets(prev => {
      const idx = prev.findIndex(w => w.id === id);
      if (idx < 0) return prev;
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr.map((w, i) => ({ ...w, order: i }));
    });
  };

  const saveLayout = async () => {
    try { await dashboardAPI.saveWidgets(widgets); toast.success('Dashboard layout saved'); setShowCustomize(false); }
    catch { toast.error('Failed to save layout'); }
  };

  const submitJournalEntry = async () => {
    if (!jeForm.debit_account || !jeForm.credit_account || !jeForm.amount) { toast.error('Fill all fields'); return; }
    setJeSaving(true);
    try {
      await api.post('/accounting/journal-entry', {
        date: jeForm.date, narration: jeForm.narration, voucher_type: 'journal',
        lines: [
          { account_code: jeForm.debit_account, debit: parseFloat(jeForm.amount), credit: 0 },
          { account_code: jeForm.credit_account, debit: 0, credit: parseFloat(jeForm.amount) },
        ]
      });
      toast.success('Journal entry created');
      setJeForm({ date: new Date().toISOString().split('T')[0], narration: '', debit_account: '', credit_account: '', amount: '' });
      api.get('/accounting/journal-entries', { params: { limit: 5 } }).then(r => setRecentJournals(r.data?.entries || [])).catch(() => {});
      loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create entry'); }
    finally { setJeSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>;

  if (!stats || (stats.total_invoices === 0 && stats.total_receipts === 0)) {
    return (
      <div data-testid="dashboard-empty" className="max-w-md mx-auto text-center py-24">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-600 to-purple-400 flex items-center justify-center mx-auto mb-5 shadow-lg shadow-purple-200">
          <span className="font-heading font-black text-white text-xl">T</span>
        </div>
        <h2 className="font-heading font-semibold text-2xl tracking-tight text-[hsl(var(--foreground))] mb-2">Welcome to Transsolution</h2>
        <p className="text-gray-500 text-sm mb-8">Get started by creating your first receipt or invoice.</p>
        <div className="flex gap-3 justify-center">
          <button onClick={() => navigate('/receipts/new')} className="btn-secondary flex items-center gap-2"><Plus className="w-4 h-4" /> New Receipt</button>
          <button onClick={() => navigate('/invoices/new')} className="btn-accent flex items-center gap-2"><Plus className="w-4 h-4" /> New Invoice</button>
        </div>
      </div>
    );
  }

  const statusDist = (stats.status_distribution || []).filter(s => s.value > 0);
  const sortedWidgets = [...widgets].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div data-testid="dashboard" className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-semibold text-2xl tracking-tight text-[hsl(var(--foreground))]">Dashboard</h1>
          <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-purple-400 mt-1">Welcome, {user?.name}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowCustomize(!showCustomize)} className="btn-secondary flex items-center gap-2 text-[13px]" data-testid="customize-dashboard-btn">
            <Settings2 className="w-3.5 h-3.5" /> Customize
          </button>
          <button onClick={() => navigate('/voucher-entries')} className="btn-secondary flex items-center gap-2 text-[13px]" data-testid="new-journal-btn"><ClipboardList className="w-3.5 h-3.5" /> Journal Entry</button>
          <button onClick={() => navigate('/invoices/new')} className="btn-accent flex items-center gap-2 text-[13px]" data-testid="new-invoice-btn"><FileText className="w-3.5 h-3.5" /> New Invoice</button>
        </div>
      </div>

      {/* Customization Panel */}
      {showCustomize && (
        <div className="glass-card rounded-xl p-4 space-y-3" data-testid="widget-customizer">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-purple-700 flex items-center gap-2"><Settings2 className="w-4 h-4" /> Customize Dashboard — Toggle widgets on/off</h3>
            <div className="flex gap-2">
              <button onClick={() => setWidgets(ALL_WIDGETS)} className="text-xs text-gray-500 hover:text-purple-600">Reset</button>
              <button onClick={saveLayout} className="btn-primary text-xs px-4 py-1.5" data-testid="save-layout-btn">Save Layout</button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {sortedWidgets.map((w, i) => (
              <div key={w.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-all ${w.pinned ? 'border-purple-300 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-200 bg-gray-50 dark:bg-gray-800 opacity-50'}`}>
                <div className="flex flex-col">
                  <button onClick={() => moveWidget(w.id, -1)} disabled={i === 0} className="text-gray-400 hover:text-purple-500 disabled:opacity-20"><ChevronUp className="w-3 h-3" /></button>
                  <button onClick={() => moveWidget(w.id, 1)} disabled={i === sortedWidgets.length - 1} className="text-gray-400 hover:text-purple-500 disabled:opacity-20"><ChevronDown className="w-3 h-3" /></button>
                </div>
                <span className="flex-1 font-medium text-gray-700 dark:text-gray-300">{w.label}</span>
                <button onClick={() => toggleWidget(w.id)} data-testid={`toggle-widget-${w.id}`}>
                  {w.pinned ? <Eye className="w-3.5 h-3.5 text-purple-500" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Render widgets in order */}
      {sortedWidgets.filter(w => w.pinned).map(w => {
        switch (w.id) {
          case 'party_filter':
            return (
              <div key={w.id} className="flex items-center gap-3 glass-card rounded-xl px-3 py-2">
                <Search className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                <select value={partyFilter} onChange={e => applyPartyFilter(e.target.value)} className="form-input border-0 bg-transparent focus:ring-0 py-0 px-0 text-sm" data-testid="dashboard-party-filter">
                  <option value="">All Parties / Clients</option>
                  {parties.map(p => <option key={p.party_id} value={p.name}>{p.name} ({p.type})</option>)}
                </select>
                {partyFilter && <button onClick={() => applyPartyFilter('')} className="p-1 hover:bg-purple-50 rounded"><X className="w-3.5 h-3.5 text-gray-400" /></button>}
              </div>
            );

          case 'metrics':
            return (
              <div key={w.id} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                {[
                  { label: 'Receipts', value: stats.total_receipts, icon: Receipt },
                  { label: 'Invoices', value: stats.total_invoices, icon: FileText },
                  { label: 'Revenue', value: `Rs.${formatCurrency(stats.total_revenue)}`, icon: DollarSign },
                  { label: 'Pending', value: stats.pending_count, icon: Clock },
                  { label: 'Overdue', value: stats.overdue_count, icon: AlertTriangle, accent: stats.overdue_count > 0 },
                ].map((m, i) => (
                  <div key={i} className="metric-card" data-testid={`metric-${m.label.toLowerCase()}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-[10px] font-bold text-purple-400 uppercase tracking-[0.12em]">{m.label}</p>
                        <p className={`text-xl font-bold font-mono mt-1.5 ${m.accent ? 'text-red-600' : 'text-[hsl(var(--foreground))]'}`}>{m.value}</p>
                      </div>
                      <m.icon className={`w-4 h-4 ${m.accent ? 'text-red-400' : 'text-purple-300'}`} strokeWidth={1.5} />
                    </div>
                  </div>
                ))}
              </div>
            );

          case 'financial':
            return isAdmin && stats.receivables !== undefined ? (
              <div key={w.id} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Receivables', value: stats.receivables, icon: ArrowUpCircle, positive: true },
                  { label: 'Payables', value: stats.payables, icon: ArrowDownCircle, positive: false },
                  { label: 'Net Balance', value: Math.abs(stats.net_balance), icon: Wallet, positive: stats.net_balance >= 0 },
                ].map((item, i) => (
                  <div key={i} className="metric-card">
                    <p className="text-[10px] font-bold text-purple-400 uppercase tracking-[0.12em]">{item.label}</p>
                    <p className={`text-xl font-bold font-mono mt-1 ${item.positive ? 'text-emerald-600' : 'text-red-600'}`}>Rs.{formatCurrency(item.value)}</p>
                  </div>
                ))}
              </div>
            ) : null;

          case 'tax_overview':
            return isAdmin ? (
              <div key={w.id} className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="financial-overview">
                {[
                  { label: 'GST Payable', value: `Rs.${formatCurrency(gstPayable?.total || 0)}`, icon: IndianRupee, nav: '/gst-returns' },
                  { label: 'TDS Payable', value: `Rs.${formatCurrency(tdsData?.tds_payable || 0)}`, icon: FileCheck, nav: '/tds-returns' },
                  { label: 'TDS Receivable', value: `Rs.${formatCurrency(tdsData?.tds_receivable || 0)}`, icon: TrendingUp, nav: '/tds-returns' },
                  { label: 'Unadj. Advances', value: txSummary?.total_advances || 0, icon: Wallet, nav: '/advance-summary' },
                ].map((item, i) => (
                  <div key={i} onClick={() => navigate(item.nav)} className="metric-card cursor-pointer" data-testid={`fin-${item.label.toLowerCase().replace(/[\s.]/g, '-')}`}>
                    <div className="flex items-start justify-between">
                      <div><p className="text-[10px] font-bold text-purple-400 uppercase tracking-[0.12em]">{item.label}</p><p className="text-lg font-bold font-mono mt-1">{item.value}</p></div>
                      <item.icon className="w-4 h-4 text-purple-300" />
                    </div>
                  </div>
                ))}
              </div>
            ) : null;

          case 'revenue_chart':
            return (
              <div key={w.id} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2 glass-card rounded-xl p-4 sm:p-6" data-testid="revenue-chart">
                  <h3 className="font-heading font-semibold text-sm text-[hsl(var(--foreground))] mb-4">Monthly Revenue</h3>
                  {stats.monthly_data?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={stats.monthly_data} barCategoryGap="20%">
                        <CartesianGrid vertical={false} strokeDasharray="0" stroke="hsl(270 30% 90%)" strokeOpacity={0.3} />
                        <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                        <Tooltip formatter={v => [`Rs. ${formatCurrency(v)}`, 'Revenue']} contentStyle={{ borderRadius: '10px', border: '1px solid hsl(270 30% 90%)', boxShadow: '0 4px 12px rgba(148,0,211,0.1)', background: 'rgba(255,255,255,0.95)' }} />
                        <Bar dataKey="revenue" fill="url(#purpleGrad)" radius={[8,8,0,0]} />
                        <defs><linearGradient id="purpleGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#9400D3" /><stop offset="100%" stopColor="#B855E0" /></linearGradient></defs>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">No data</div>}
                </div>
                {isPinned('status_pie') && (
                  <div className="glass-card rounded-xl p-6" data-testid="status-pie">
                    <h3 className="font-heading font-semibold text-sm text-[hsl(var(--foreground))] mb-4">Invoice Status</h3>
                    {statusDist.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart><Pie data={statusDist} cx="50%" cy="50%" innerRadius={40} outerRadius={72} dataKey="value" label={({ percent }) => percent > 0.05 ? `${(percent*100).toFixed(0)}%` : ''} labelLine={false} strokeWidth={0}>{statusDist.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="none" />)}</Pie><Tooltip /><Legend iconType="circle" iconSize={6} wrapperStyle={{ fontSize: 11 }} /></PieChart>
                      </ResponsiveContainer>
                    ) : <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">No data</div>}
                  </div>
                )}
              </div>
            );

          case 'quick_journal':
            return (
              <div key={w.id} className="glass-card rounded-xl p-5" data-testid="quick-journal-entry">
                <h3 className="font-heading font-semibold text-sm text-[hsl(var(--foreground))] mb-3 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-purple-500" /> Quick Journal Entry</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 items-end">
                  <div><label className="form-label">Date</label><input type="date" value={jeForm.date} onChange={e => setJeForm(f => ({...f, date: e.target.value}))} className="form-input text-xs py-2" data-testid="je-date" /></div>
                  <div><label className="form-label">Debit Account</label>
                    <select value={jeForm.debit_account} onChange={e => setJeForm(f => ({...f, debit_account: e.target.value}))} className="form-input text-xs py-2" data-testid="je-debit">
                      <option value="">Select...</option>{accounts.map(a => <option key={a.code} value={a.code}>{a.code} - {a.name}</option>)}
                    </select>
                  </div>
                  <div><label className="form-label">Credit Account</label>
                    <select value={jeForm.credit_account} onChange={e => setJeForm(f => ({...f, credit_account: e.target.value}))} className="form-input text-xs py-2" data-testid="je-credit">
                      <option value="">Select...</option>{accounts.map(a => <option key={a.code} value={a.code}>{a.code} - {a.name}</option>)}
                    </select>
                  </div>
                  <div><label className="form-label">Amount</label><input type="number" value={jeForm.amount} onChange={e => setJeForm(f => ({...f, amount: e.target.value}))} placeholder="0.00" className="form-input text-xs py-2" data-testid="je-amount" /></div>
                  <div><label className="form-label">Narration</label><input value={jeForm.narration} onChange={e => setJeForm(f => ({...f, narration: e.target.value}))} placeholder="Description" className="form-input text-xs py-2" data-testid="je-narration" /></div>
                  <button onClick={submitJournalEntry} disabled={jeSaving} className="btn-primary py-2 text-xs" data-testid="je-submit">{jeSaving ? 'Saving...' : 'Post Entry'}</button>
                </div>
              </div>
            );

          case 'recent_journals':
            return (
              <div key={w.id} className="glass-card rounded-xl overflow-hidden" data-testid="recent-journals">
                <div className="px-5 py-3 flex items-center justify-between border-b border-purple-100 dark:border-purple-900/30">
                  <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><BookOpen className="w-3.5 h-3.5 text-purple-500" /> Recent Journal Entries</h3>
                  <button onClick={() => navigate('/voucher-entries')} className="text-[10px] text-purple-600 font-bold flex items-center gap-1 uppercase tracking-[0.1em]">View all <ArrowRight className="w-3 h-3" /></button>
                </div>
                <table className="w-full data-table"><thead><tr><th>Date</th><th>Voucher #</th><th>Type</th><th>Narration</th><th className="text-right">Amount</th></tr></thead>
                <tbody>{recentJournals.map((j, i) => (
                  <tr key={i} className="cursor-pointer hover:bg-purple-50/30" onClick={() => navigate('/voucher-entries')}>
                    <td className="text-sm">{j.date}</td><td className="font-mono text-xs text-purple-600">{j.voucher_number}</td>
                    <td><span className="text-[10px] font-semibold capitalize px-2 py-0.5 rounded-full bg-purple-50 text-purple-600">{j.voucher_type}</span></td>
                    <td className="text-sm text-gray-500 max-w-[200px] truncate">{j.narration || '-'}</td>
                    <td className="text-right font-mono text-sm">Rs.{formatCurrency(j.lines?.[0]?.debit || 0)}</td>
                  </tr>
                ))}{recentJournals.length === 0 && <tr><td colSpan={5} className="text-center text-gray-400 py-6 text-sm">No journal entries</td></tr>}</tbody></table>
              </div>
            );

          case 'quick_actions':
            return (
              <div key={w.id} className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="quick-actions">
                {[
                  { label: 'New Invoice', icon: FileText, path: '/invoices/new', color: 'from-purple-600 to-purple-400' },
                  { label: 'New Receipt', icon: Receipt, path: '/receipts/new', color: 'from-emerald-600 to-emerald-400' },
                  { label: 'Journal Entry', icon: ClipboardList, path: '/voucher-entries', color: 'from-blue-600 to-blue-400' },
                  { label: 'View Ledger', icon: Landmark, path: '/ledger', color: 'from-amber-600 to-amber-400' },
                ].map((a, i) => (
                  <button key={i} onClick={() => navigate(a.path)} className={`flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r ${a.color} text-white font-medium text-sm transition-all hover:scale-[1.02] hover:shadow-lg`} data-testid={`quick-${a.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <a.icon className="w-5 h-5" />{a.label}
                  </button>
                ))}
              </div>
            );

          case 'employee_activity':
            return isAdmin && (stats.employee_receipts?.length > 0 || stats.employee_invoices?.length > 0) ? (
              <div key={w.id} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="glass-card rounded-xl p-5" data-testid="emp-receipt-activity">
                  <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2"><Users className="w-3.5 h-3.5 text-purple-500" /> Employee Receipts</h3>
                  {stats.employee_receipts?.map((e, i) => (
                    <div key={i} className="flex justify-between items-center py-1.5 border-b border-purple-50 dark:border-purple-900/20 last:border-0">
                      <span className="text-sm">{e.name}</span><span className="font-mono text-sm font-semibold">{e.count}</span>
                    </div>
                  ))}
                </div>
                <div className="glass-card rounded-xl p-5" data-testid="emp-invoice-activity">
                  <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2"><Users className="w-3.5 h-3.5 text-purple-500" /> Employee Invoices</h3>
                  {stats.employee_invoices?.map((e, i) => (
                    <div key={i} className="flex justify-between items-center py-1.5 border-b border-purple-50 dark:border-purple-900/20 last:border-0">
                      <span className="text-sm">{e.name}</span><div className="text-right"><span className="font-mono text-sm font-semibold">{e.count}</span><span className="font-mono text-xs text-gray-400 ml-2">Rs.{formatCurrency(e.total)}</span></div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;

          case 'recent_receipts':
            return (
              <div key={w.id} className="glass-card rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-purple-100 dark:border-purple-900/30 flex items-center justify-between">
                  <h3 className="font-heading font-semibold text-sm">Recent Receipts</h3>
                  <button onClick={() => navigate('/receipts')} className="text-[10px] text-purple-600 font-bold flex items-center gap-1 uppercase tracking-[0.1em]" data-testid="view-all-receipts">View all <ArrowRight className="w-3 h-3" /></button>
                </div>
                <table className="w-full data-table" data-testid="recent-receipts-table"><thead><tr><th>Receipt #</th><th>Date</th><th>Consignee</th><th>By</th></tr></thead>
                <tbody>{stats.recent_receipts?.map(r => <tr key={r.receipt_id} className="cursor-pointer" onClick={() => navigate(`/receipts/${r.receipt_id}`)}><td className="font-mono text-sm">{r.receipt_number}</td><td className="text-sm text-gray-500">{r.booking_date}</td><td className="text-sm">{r.consignee_name}</td><td className="text-[11px] text-gray-400">{r.created_by_name}</td></tr>)}
                {(!stats.recent_receipts?.length) && <tr><td colSpan={4} className="text-center text-gray-400 py-6 text-sm">No receipts</td></tr>}</tbody></table>
              </div>
            );

          case 'recent_invoices':
            return (
              <div key={w.id} className="glass-card rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-purple-100 dark:border-purple-900/30 flex items-center justify-between">
                  <h3 className="font-heading font-semibold text-sm">Recent Invoices</h3>
                  <button onClick={() => navigate('/invoices')} className="text-[10px] text-purple-600 font-bold flex items-center gap-1 uppercase tracking-[0.1em]" data-testid="view-all-invoices">View all <ArrowRight className="w-3 h-3" /></button>
                </div>
                <table className="w-full data-table" data-testid="recent-invoices-table"><thead><tr><th>Invoice #</th><th>Date</th><th>Client</th><th className="text-right">Amount</th></tr></thead>
                <tbody>{stats.recent_invoices?.map(inv => <tr key={inv.invoice_id} className="cursor-pointer" onClick={() => navigate(`/invoices/${inv.invoice_id}`)}><td className="font-mono text-sm">{inv.invoice_number}</td><td className="text-sm text-gray-500">{inv.invoice_date}</td><td className="text-sm">{inv.billed_to_name}</td><td className="text-right font-mono text-sm">Rs.{formatCurrency(inv.grand_total)}</td></tr>)}
                {(!stats.recent_invoices?.length) && <tr><td colSpan={4} className="text-center text-gray-400 py-6 text-sm">No invoices</td></tr>}</tbody></table>
              </div>
            );

          default: return null;
        }
      })}
    </div>
  );
}
